import TabContainer from '@/components/common/TabContainer';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Nav, NavItem } from 'reactstrap';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  IAddEmployeeForm,
  IEmployeePageStates,
  IEmployeeResponse,
  ISalaryPageStates,
} from '@/interfaces/employee.model';
import EmployeeBasicDetailsStep1 from './onBoardFormSteps/EmployeeBasicDetailsStep1';
import EmployeePermissionStep2 from './onBoardFormSteps/EmployeePermissionStep2';
import { showToastNotification } from '@/utility/helper';
import EmployeeAdvanceDetailsStep3 from './onBoardFormSteps/EmployeeAdvanceDetailsStep3';
import EmployeeAddressDetailsStep4 from './onBoardFormSteps/EmployeeAddressDetailsStep4';
import EmployeeBankDetailsStep5 from './onBoardFormSteps/EmployeeBankDetailsStep5';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IDocument, IUser } from '@/interfaces/user.model';
import moment from 'moment';
import EmployeeNomineeDetailsStep6 from './onBoardFormSteps/EmployeeNomineeDetailsStep6';
import EmployeePreviousWorkStep7 from './onBoardFormSteps/EmployeePreviousWorkStep7';
import EmployeeDocumentsStep8 from './onBoardFormSteps/EmployeeDocumentsStep8';
import EmployeeSalaryDetailStep9 from './onBoardFormSteps/EmployeeSalaryDetailStep9';
import FullPageLoader from '@/components/loader/FullPageLoader';
const AddEmployeeOnBoard: React.FC = () => {
  const { id } = useParams();
  const methods = useForm<IAddEmployeeForm>({
    mode: 'onSubmit',
  });
  const [salaryPageStates, setSalaryPageStates] = useState<ISalaryPageStates>({
    isSalaryCreated: false,
    isLoading: false,
    isLoadingSalaryCreation: false,
    isLoadingSalaryUpdation: false,
    isLoadingGetUserSalary: false,
    isLoadingOfferLetter: false,
  });
  const [existingDocuments, setExistingDocuments] = useState<IDocument[]>([]);
  const [employeeDetail, setEmployeeDetails] = useState<IUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<File | string | null>(null);
  const { reset, handleSubmit, setValue } = methods;
  const [pageStates, setPageStates] = useState<IEmployeePageStates>({
    activeTab: 1,
    empId: null,
  });
  const formSteps = [
    {
      tabId: 1,
      label: 'Basic Details',
    },
    {
      tabId: 2,
      label: 'Permissions',
    },
    {
      tabId: 3,
      label: 'Advance Details',
    },
    {
      tabId: 4,
      label: 'Address',
    },
    {
      tabId: 5,
      label: 'Bank Details',
    },
    {
      tabId: 6,
      label: 'Nominee Details',
    },
    {
      tabId: 7,
      label: 'Previous Work',
    },
    {
      tabId: 8,
      label: 'Documents',
    },
    {
      tabId: 9,
      label: 'Salary Detail',
    },
  ];

  useEffect(() => {
    if (id) {
      setPageStates((prev) => ({
        ...prev,
        empId: id,
      }));
    } else {
      setValue('status', 'current');
    }
  }, [id, setValue]);
  const onSubmit: SubmitHandler<IAddEmployeeForm> = () => {
    if (!imageFile) {
      showToastNotification('danger', 'Please add image');
      return;
    }
    setPageStates((prev) => ({
      ...prev,
      activeTab: pageStates.activeTab + 1,
    }));
  };
  const nextStep = () => {
    setPageStates((prev) => ({
      ...prev,
      activeTab: pageStates.activeTab + 1,
    }));
  };
  const getEmployeeDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: IEmployeeResponse = await callApi(
        API_URL + API_ENDPOINT.getProfile + `?userId=${id}`,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        const data = response?.data;
        setEmployeeDetails(response?.data);
        reset({
          firstName: data?.name.split(' ')[0],
          lastName: data?.name.split(' ')[1],
          bloodGroup: data?.bloodGroup ?? '',
          dob: data?.dob ?? '',
          personalEmail: data?.personalEmail ?? '',
          email: data?.email,
          employeeExitAt: data?.employeeExitAt ?? '',
          motherName: data?.userdetail?.motherName ?? '',
          fatherName: data?.userdetail?.fatherName ?? '',
          gender: data?.gender,
          isMarried: data?.isMarried?.toString(),
          skillType: data?.skillType ?? '',
          joiningDate:
            (data?.joiningDate &&
              moment(data?.joiningDate).format('YYYY-MM-DD')) ??
            '-',
          isSameAsPermanentAddress: data?.userdetail?.isSameAsPermanentAddress,
          profilePhoto: data?.profilePhoto,
          phone: data?.phone,
          status: data?.status,
          officeId: data?.officeId ?? '',
          departmentId: data?.departmentId ?? '',
          designationId: data?.designationId ?? '',
          employeeCode: data?.employeeCode ?? '',
          address: data?.userdetail?.address ?? '',
          state: data?.userdetail?.state ?? '',
          city: data?.userdetail?.city ?? '',
          companyId: data?.companyId ?? '',
          ecpName: data?.userdetail?.ecpName,
          ecpPhone: data?.userdetail?.ecpPhone,
          permanentAddress: data?.userdetail?.permanentAddress ?? '',
          pinCode: data?.userdetail?.pinCode ?? '',
          permanentCity: data?.userdetail?.permanentCity ?? '',
          permanentPinCode: data?.userdetail?.permanentPinCode ?? '',
          permanentState: data?.userdetail?.permanentState ?? '',
          uanNumber: data?.userdetail?.uanNumber ?? '',
          aadharNumber: data?.userdetail?.aadharNumber ?? '',
          esicNumber: data?.userdetail?.esicNumber ?? '',
          drivingLicenseNumber: data?.userdetail?.drivingLicenseNumber ?? '',
          insuranceNumber: data?.userdetail?.insuranceNumber ?? '',
          panNumber: data?.userdetail?.panNumber ?? '',
          pfNumber: data?.userdetail?.pfNumber ?? '',
          bankName: data?.userdetail?.bankName ?? '',
          accountNumber: data?.userdetail?.accountNumber ?? '',
          accountHolderName: data?.userdetail?.accountHolderName ?? '',
          ifscCode: data?.userdetail?.ifscCode ?? '',
          branchAddress: data?.userdetail?.branchAddress ?? '',
          nomineeDob: data?.userdetail?.nomineeDob ?? '',
          nomineeName: data?.userdetail?.nomineeName ?? '',
          nomineeRelation: data?.userdetail?.nomineeRelation ?? '',
          nomineePhone: data?.userdetail?.nomineePhone ?? '',
          shiftId: data?.shiftId ?? '',
        });
        setImageFile(data?.profilePhoto);
        setExistingDocuments(data?.documents);
        setSalaryPageStates((prev) => ({
          ...prev,
          isSalaryCreated: response?.data?.isSalaryCreated,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    if (id) {
      getEmployeeDetail();
    }
  }, [id, getEmployeeDetail]);

  return (
    <Layout>
      <div className="content container-fluid">
        <BackButton label="Employee" isEdit={Boolean(id)} />
        <CardLayout cardStyle="mb-4">
          <div className="wizard-tabs">
            <Nav tabs className="nav nav-pills ">
              {formSteps.map((step, index) => {
                return (
                  <>
                    <NavItem key={step.tabId}>
                      <Link
                        to={'#'}
                        className={
                          pageStates.activeTab === step.tabId
                            ? 'border-0 p-2 active  nav-link'
                            : ' nav-link'
                        }
                        style={{ cursor: 'pointer' }}
                        onClick={
                          pageStates.empId || step?.tabId == 1
                            ? () =>
                                setPageStates((prev) => ({
                                  ...prev,
                                  activeTab: step?.tabId,
                                }))
                            : () => {
                                const isUpdateFlow = !!id;
                                if (isUpdateFlow) {
                                  setPageStates((prev) => ({
                                    ...prev,
                                    activeTab: step?.tabId,
                                  }));
                                } else if (
                                  !pageStates.empId &&
                                  (step?.tabId === 1 || step?.tabId === 2)
                                ) {
                                  setPageStates((prev) => ({
                                    ...prev,
                                    activeTab: step?.tabId,
                                  }));
                                } else if (!pageStates.empId) {
                                  showToastNotification(
                                    'danger',
                                    'Please complete the creation process before proceeding.',
                                  );
                                } else if (
                                  pageStates.empId &&
                                  step?.tabId > 2
                                ) {
                                  setPageStates((prev) => ({
                                    ...prev,
                                    activeTab: step?.tabId,
                                  }));
                                } else {
                                  showToastNotification(
                                    'info',
                                    'Basic Details and Permissions can only be updated later.',
                                  );
                                }
                              }
                        }
                      >
                        <h6>Step {step?.tabId}</h6>
                        <h5>{step.label}</h5>
                      </Link>
                    </NavItem>{' '}
                    {formSteps?.length - 1 != index && (
                      <li className="nav-item">
                        <div className="border-top-item" />
                      </li>
                    )}
                  </>
                );
              })}
            </Nav>
          </div>
        </CardLayout>
        {/* <CardLayout> */}
        {isLoading ? (
          <FullPageLoader />
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <TabContainer activeTab={pageStates.activeTab} tabId={1}>
                <EmployeeBasicDetailsStep1
                  nextStep={nextStep}
                  setImageFile={setImageFile}
                  imageFile={imageFile}
                  empId={pageStates.empId}
                />
              </TabContainer>
              {pageStates.activeTab == 2 && (
                <TabContainer activeTab={pageStates.activeTab} tabId={2}>
                  <EmployeePermissionStep2
                    nextStep={nextStep}
                    imageFile={imageFile}
                    setPageStates={setPageStates}
                  />
                </TabContainer>
              )}
              <TabContainer activeTab={pageStates.activeTab} tabId={3}>
                <EmployeeAdvanceDetailsStep3
                  empId={pageStates?.empId}
                  nextStep={nextStep}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={4}>
                <EmployeeAddressDetailsStep4
                  nextStep={nextStep}
                  empId={pageStates.empId}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={5}>
                <EmployeeBankDetailsStep5
                  nextStep={nextStep}
                  empId={pageStates.empId}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={6}>
                <EmployeeNomineeDetailsStep6
                  nextStep={nextStep}
                  empId={pageStates.empId}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={7}>
                <EmployeePreviousWorkStep7
                  nextStep={nextStep}
                  empId={pageStates.empId}
                  previousWork={employeeDetail?.previousworks}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={8}>
                <EmployeeDocumentsStep8
                  existingDocuments={existingDocuments}
                  setExistingDocuments={setExistingDocuments}
                  empId={pageStates.empId}
                  nextStep={nextStep}
                />
              </TabContainer>
              <TabContainer activeTab={pageStates.activeTab} tabId={9}>
                <EmployeeSalaryDetailStep9
                  pageStates={salaryPageStates}
                  empId={pageStates.empId}
                  employeeDetail={employeeDetail}
                  activeTab={pageStates.activeTab}
                  getEmployeeDetail={getEmployeeDetail}
                  setPageStates={setSalaryPageStates}
                />
              </TabContainer>
            </form>
          </FormProvider>
        )}
      </div>
    </Layout>
  );
};
export default AddEmployeeOnBoard;
